import CloseIcon from "@mui/icons-material/Close";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import SearchIcon from "@mui/icons-material/Search";
import Box from "@mui/material/Box";
import InputAdornment from "@mui/material/InputAdornment";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import { setImpersonationDetails, setInnCode } from "components/app/App.slice";
import ThemeContext from "components/app/Context";
import Button from "components/common/Button";
import Scroller from "components/common/Scroller/InfiniteScroller";
import Toggle from "components/common/Toggle";
import Typography from "components/common/Typography";
import MultiSelectFilter from "components/features/SME/home/multiSelectFilter";
import { useTranslate } from "hooks/useTranslate";
import { uniq } from "lodash";
import { useContext, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "redux/hooks";
import { tokens } from "styles/Tokens";
import { ABOVE_PROP_USER_TYPE, ENTER_KEY_CODE, includeAboveProperty, minSearchStrLength } from "utils/CommonConstants";
import { getUserTypeValue } from "utils/CommonFunctions";
import { fetchPropertyDetails, setUserDashboardFilters } from "../userdashboard.slice";
import "./MemberList.scss";

const MemberList = () => {
  const dispatch = useAppDispatch();
  const [page, setPage] = useState(0);
  const [searchParams] = useSearchParams();
  const translate = useTranslate();
  const inncode = searchParams.get("inncode");
  const { isMobileScreen } = useContext(ThemeContext);
  const [memberInputValue, setMemberInputValue] = useState("");

  const isSmeUser = useAppSelector((state) => state.app.isSmeUser);
  const userRoles = useAppSelector((state) => state.app.userRoles);

  const { propertyDetails, filters } = useAppSelector((state) => state.userDashboard);

  const { users } = propertyDetails;
  const isFiltered = filters.hasOwnProperty("roles");
  // @ts-ignore

  const formPayload = (page: number, roles: string[], searchText: string, userType: string) => {
    return {
      innCodes: inncode,
      page,
      roles,
      searchText,
      userType,
      includecorpusers: false
    };
  };

  const fetchMore = () => {
    const roles = filters?.roles.join(",");

    dispatch(
      fetchPropertyDetails(
        formPayload(page + 1, roles, memberInputValue, getUserTypeValue(filters[includeAboveProperty]))
      )
    );
    setPage((pageNo) => pageNo + 1);
  };

  const handleToggleChange = (val: boolean) => {
    const roles = filters?.roles.join(",");
    dispatch(setUserDashboardFilters({ ...filters, includeAboveProperty: val }));
    dispatch(fetchPropertyDetails(formPayload(0, roles, memberInputValue, getUserTypeValue(val))));
    setPage(0);
  };

  const handleFilterChange = (val: any) => {
    const roles = val.join(",");
    dispatch(setUserDashboardFilters({ ...filters, roles: val }));
    dispatch(
      fetchPropertyDetails(formPayload(0, roles, memberInputValue, getUserTypeValue(filters[includeAboveProperty])))
    );
    setPage(0);
  };

  const freshSearch = () => {
    const roles = filters.roles.join(",");
    dispatch(fetchPropertyDetails(formPayload(0, roles, "", getUserTypeValue(filters[includeAboveProperty]))));
  };

  const handleMemberInputChange = (e: any) => {
    const { value } = e.target;

    setMemberInputValue(value);

    if (!value.trim()) {
      freshSearch();
    }
  };

  const onSearch = (e: any) => {
    const { value } = e.target;
    const roles = filters.roles.join(",");

    if (e.charCode === ENTER_KEY_CODE || e.key === "Enter") {
      if (value.trim() && value.trim().length > minSearchStrLength) {
        dispatch(fetchPropertyDetails(formPayload(0, roles, value, getUserTypeValue(filters[includeAboveProperty]))));
      }

      if (!value.trim()) {
        freshSearch();
      }
    }
  };

  useEffect(() => {
    setMemberInputValue("");
  }, [inncode]);

  const handleImpersonateUserClick = (member: any) => {
    const isAbovePropertyUser = String(member?.lobbyUserType).toLowerCase() === ABOVE_PROP_USER_TYPE;

    dispatch(
      setImpersonationDetails({
        isImpersonated: true,
        navigateToURL: "inncodeUsers" + window.location.search,
        userDetails: { ...member, isAbovePropertyUser, isSmeUser }
      })
    );
    dispatch(setInnCode(inncode));
  };

  const getToggleView = () => {
    return (
      <>
        <Toggle
          onChange={handleToggleChange}
          labelOn={translate("common.includeAboveProperty")}
          labelOff={translate("common.excludeAboveProperty")}
          isToggleEnabled={filters[includeAboveProperty]}
          isLabelLeftSide
          sx={{ marginLeft: "8px" }}
        />
      </>
    );
  };

  const scrollToTop = () => {
    //TODO will make it generic
    let divs = document.getElementById("memberList");
    if (divs) divs.scrollTop = 0;
  };

  useEffect(() => {
    setPage(0);
    scrollToTop();
  }, [inncode]);

  return (
    <Box
      sx={{
        backgroundColor: isMobileScreen ? "transparent" : tokens.Background.General.Primary,
        pb: users?.records?.length ? 0 : "24px",
        borderRadius: "12px",
        overflow: "auto",
        maxHeight: isMobileScreen ? "calc(100vh - 270px)" : "100%"
      }}
      id="memberList"
    >
      <div className="custom-stack">
        <TextField
          onKeyUp={onSearch}
          value={memberInputValue}
          onChange={handleMemberInputChange}
          placeholder="Search by Member"
          sx={{ height: "44px" }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
            endAdornment: memberInputValue.length ? (
              <InputAdornment position="end">
                <CloseIcon
                  sx={{ cursor: "pointer", fontSize: "20px" }}
                  onClick={() => {
                    setMemberInputValue("");
                    freshSearch();
                    setPage(0);
                  }}
                />
              </InputAdornment>
            ) : null
          }}
        />
        <Box
          display={"flex"}
          paddingTop={isMobileScreen ? "8px" : "none"}
          flexDirection={"row"}
          alignItems={"center"}
          gap={"8px"}
          justifyContent={"center"}
        >
          {!isMobileScreen && getToggleView()}
          <MultiSelectFilter
            label="Role"
            options={uniq(userRoles?.roles?.map((el: any) => el?.roleName))}
            onSubmit={handleFilterChange}
            isFiltered={isFiltered}
            selectedOptions={filters?.roles}
          />
        </Box>
      </div>
      {isMobileScreen && getToggleView()}

      {!isMobileScreen && (
        <Stack
          spacing={2}
          direction="row"
          alignItems="center"
          sx={{
            padding: "16px 24px",
            borderTop: `1px solid ${tokens.Border.General.Tertiary}`,
            borderBottom: `1px solid ${tokens.Border.General.Tertiary}`
          }}
        >
          <Typography variant="body2" color={tokens.Text.General.Tertiary} sx={{ flex: 3 }}>
            Member
          </Typography>
          <Typography variant="body2" color={tokens.Text.General.Tertiary} sx={{ flex: 6 }}>
            Role
          </Typography>
        </Stack>
      )}

      <Scroller
        dataLength={users?.records?.length}
        loadMore={fetchMore}
        totalList={users?.count}
        label=""
        scrollableTarget="memberList"
      >
        {users?.records?.map((member: any) =>
          isMobileScreen ? (
            <Stack
              key={member?.hltId}
              direction="row"
              alignItems="center"
              sx={{ textDecoration: "none", mb: "12px", mt: isMobileScreen ? "18px" : "none" }}
              onClick={() => handleImpersonateUserClick(member)}
            >
              <Typography
                variant="body2"
                showBold
                color={tokens.Text.General.PrimaryHeadline}
                sx={{ flex: 1, mb: "4px" }}
              >
                {member?.displayName}
                <Typography variant="body2" showBold={false} color={tokens.Text.General.Tertiary}>
                  {member?.roles?.join(", ")}
                </Typography>
              </Typography>
              <NavigateNextIcon
                sx={{ fontSize: "30px", ml: "8px", fontWeight: 200, color: tokens.Icon.General.Color }}
              />
            </Stack>
          ) : (
            <Stack key={member?.hltId} spacing={2} direction="row" alignItems="center" sx={{ padding: "16px 24px" }}>
              <Typography variant="body2" showBold color={tokens.Text.General.PrimaryHeadline} sx={{ flex: 3 }}>
                {member?.displayName}
              </Typography>
              <Stack spacing={2} direction="row" sx={{ flex: 6 }}>
                <Typography variant="body2" color={tokens.Text.General.Tertiary} sx={{ flex: 1 }}>
                  {member?.roles?.join(", ")}
                </Typography>
                <Button
                  variant="text"
                  onClick={() => handleImpersonateUserClick(member)}
                  sx={{ fontWeight: "bold", textDecoration: "underline" }}
                  label="Impersonate user"
                ></Button>
              </Stack>
            </Stack>
          )
        )}
      </Scroller>
    </Box>
  );
};

export default MemberList;
